// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

"use client";

import React from "react";

import SearchedTextHighlighterWithoutAutoExpansion from "@/components/shared/search/search-text-highlighter-without-auto-expansion";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";
import { ImagePreview } from "@/hub/components/image-preview";
import { DashboardTemplateMetadata } from "@/hub/types";

import { localeCompareIgnoreCase } from "@/lib/utils/string";

type DashboardTemplateCardProps = {
  dashboardTemplate: DashboardTemplateMetadata;
};

export function DashboardTemplateCard({
  dashboardTemplate,
}: DashboardTemplateCardProps) {
  const previewImage = dashboardTemplate.previews.filter(
    (p) => p.type === "image"
  )[0];
  const previewImageSrc = `/hub/dashboard/preview-image?dashboardId=${encodeURIComponent(
    dashboardTemplate.id
  )}&previewName=${encodeURIComponent(previewImage?.src || "")}`;

  return (
    <div
      className="flex flex-col rounded-md p-4 bg-[#1E1E1E] transition-colors"
      data-testid="hub.dashboard"
    >
      <div className="flex flex-col gap-0.5">
        <h2 className="text-lg font-semibold leading-normal mt-0 mb-2 transition-colors text-[#F3F3F3]">
          <SearchedTextHighlighterWithoutAutoExpansion
            text={dashboardTemplate.label}
          />
        </h2>
        <p className="text-base transition-colors">
          <SearchedTextHighlighterWithoutAutoExpansion
            text={dashboardTemplate.description}
          />
        </p>
      </div>

      {dashboardTemplate.tags.length > 0 && (
        <div className="mt-4 mb-6">
          {dashboardTemplate.tags
            .slice()
            .sort(localeCompareIgnoreCase)
            .map((t) => (
              <Tag
                key={t}
                className="inline-flex text-grey-550 items-center rounded-tag grow-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 ring-offset-background font-mono text-sm leading-4 px-1 h-4 [&>svg]:w-2.5 [&>svg]:h-2.5 group-focus:text-default group-focus:hover:text-intense hover:text-intense hover:bg-tag-subtle-hover data-[state=open]:bg-tag-subtle-hover data-[state=open]:text-intense pointer-events-none "
              >
                [<SearchedTextHighlighterWithoutAutoExpansion text={t} />]
              </Tag>
            ))}
        </div>
      )}

      {previewImage && (
        <a
          href={previewImageSrc}
          target="_blank"
          className="mt-2 cursor-zoom-in"
        >
          <ImagePreview
            imagePreview={previewImage}
            src={previewImageSrc}
            maxWidth={600}
            maxHeight={200}
          />
        </a>
      )}
    </div>
  );
}
